import React, { useContext, useState, useEffect } from 'react';
import { AppStateContext } from '../App';
import * as XLSX from 'xlsx';
import { Package, Gift, Users, Clock, UserPlus, Download } from 'lucide-react';
import AdminLayout from '../components/layout/AdminLayout';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ModernLoader from '../components/ModernLoader';

const API = '/api';

// Modal konfirmasi hapus kupon
function ConfirmModal({ open, onClose, onConfirm, message }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-6 w-full max-w-xs text-center">
        <div className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">{message}</div>
        <div className="flex gap-4 justify-center">
          <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded" onClick={onConfirm}>Hapus</button>
          <button className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 px-4 py-2 rounded" onClick={onClose}>Batal</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { isAdmin } = useContext(AppStateContext);
  const [hargaPaket, setHargaPaket] = useState({});
  const [hargaInput, setHargaInput] = useState({});
  const [kuponList, setKuponList] = useState([]);
  const [kuponInput, setKuponInput] = useState({ code: '', diskon: 0, expired: '' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [sortField, setSortField] = useState('email');
  const [sortAsc, setSortAsc] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [kuponToDelete, setKuponToDelete] = useState(null);
  const [kuponRiwayat, setKuponRiwayat] = useState([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newPaket, setNewPaket] = useState({ id: '', name: '', price: '', diskon: '' });
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserPaket, setAddUserPaket] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('paket');
  const [showDeletePaketConfirm, setShowDeletePaketConfirm] = useState(false);
  const [paketToDelete, setPaketToDelete] = useState(null);
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editPaketId, setEditPaketId] = useState(null);

  // Daftar menu admin
  const adminMenus = [
    { id: 'paket', label: 'Atur Paket', icon: Package },
    { id: 'kupon', label: 'Atur Kupon', icon: Gift },
    { id: 'user', label: 'User Premium', icon: Users },
    { id: 'riwayat', label: 'Riwayat Kupon', icon: Clock },
    { id: 'tambah-user', label: 'Tambah User Premium', icon: UserPlus },
  ];

  // Fetch data paket, kupon, user
  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    console.log('Fetching admin data...');
    Promise.all([
      fetch(`${API}/admin/paket`).then(r => r.json()).catch((e) => {
        console.error('Error fetching paket:', e);
        return {};
      }),
      fetch(`${API}/admin/kupon`).then(r => r.json()).catch((e) => {
        console.error('Error fetching kupon:', e);
        return [];
      }),
      fetch(`${API}/admin/users-premium`).then(r => {
        console.log('Users premium response status:', r.status);
        return r.json();
      }).catch((e) => {
        console.error('Error fetching users-premium:', e);
        return [];
      })
    ]).then(([paket, kupon, users]) => {
      console.log('Received data:', { paket, kupon, users });
      console.log('Users array:', users);
      console.log('Users length:', users ? users.length : 0);
      setHargaPaket(paket);
      setHargaInput(paket);
      setKuponList(kupon);
      setUsers(Array.isArray(users) ? users : []);
      setLoading(false);
    }).catch((e) => {
      console.error('Error in Promise.all:', e);
      setLoading(false);
    });
  }, [isAdmin]);

  // Fetch riwayat kupon saat mount
  useEffect(() => {
    fetch('/api/admin/kupon-riwayat')
      .then(res => res.json())
      .then(setKuponRiwayat);
  }, []);

  // Notifikasi auto-dismiss
  useEffect(() => {
    if (notif) {
      const t = setTimeout(() => setNotif(''), 2500);
      return () => clearTimeout(t);
    }
  }, [notif]);

  // Update harga paket
  const handleSimpanHarga = async () => {
    setNotif('Menyimpan harga...');
    await fetch(`${API}/admin/paket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(hargaInput)
    });
    setHargaPaket(hargaInput);
    setHargaInput(hargaInput);
    setNotif('Harga & diskon paket berhasil disimpan!');
  };

  // Tambah kupon
  const handleTambahKupon = async () => {
    // Validasi input kupon
    if (!kuponInput.code) return setNotif('Kode kupon tidak boleh kosong!');
    if (kuponList.some(k => k.code === kuponInput.code)) return setNotif('Kode kupon sudah ada!');
    if (kuponInput.diskon <= 0) return setNotif('Diskon harus lebih dari 0!');
    if (!kuponInput.expired || new Date(kuponInput.expired) <= new Date()) return setNotif('Tanggal expired tidak valid!');
    const newList = [...kuponList, kuponInput];
    setNotif('Menyimpan kupon...');
    await fetch(`${API}/admin/kupon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newList)
    });
    setKuponList(newList);
    setKuponInput({ code: '', diskon: 0, expired: '' });
    setNotif('Kupon berhasil ditambah!');
  };

  // Hapus kupon dengan modal konfirmasi
  const handleHapusKupon = (idx) => {
    setKuponToDelete(idx);
    setShowConfirm(true);
  };
  const confirmDeleteKupon = async () => {
    const idx = kuponToDelete;
    setShowConfirm(false);
    setKuponToDelete(null);
    const newList = kuponList.filter((_, i) => i !== idx);
    setNotif('Menyimpan kupon...');
    await fetch(`${API}/admin/kupon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newList)
    });
    setKuponList(newList);
    setNotif('Kupon dihapus!');
  };

  // Fungsi hitung sisa hari
  const getSisaHari = (u) => {
    let until = u.premiumUntil || u.trialEnd;
    if (!until) return '-';
    const sisa = Math.ceil((new Date(until) - new Date()) / (1000 * 60 * 60 * 24));
    return sisa > 0 ? sisa + ' hari' : 'Expired';
  };

  // Fungsi status user
  const getStatus = (u) => {
    if (u.isPremium && u.premiumUntil && new Date(u.premiumUntil) > new Date()) return 'Premium';
    if (u.trialStart && (!u.premiumUntil || new Date(u.premiumUntil) < new Date())) return 'Trial';
    return 'Nonaktif';
  };

  // Filter dan sort user
  const filteredUsers = (Array.isArray(users) ? users : []).filter(u => {
    const status = getStatus(u).toLowerCase();
    if (userStatusFilter === 'all') return true;
    return status === userStatusFilter;
  }).filter(u =>
    (u.email || '-').toLowerCase().includes(searchUser.toLowerCase()) ||
    (u.displayName || '').toLowerCase().includes(searchUser.toLowerCase())
  ).sort((a, b) => {
    let valA = a[sortField] || '';
    let valB = b[sortField] || '';
    if (sortField === 'status') {
      valA = getStatus(a);
      valB = getStatus(b);
    }
    if (sortField === 'expired') {
      valA = a.premiumUntil || a.trialEnd || '';
      valB = b.premiumUntil || b.trialEnd || '';
    }
    if (valA < valB) return sortAsc ? -1 : 1;
    if (valA > valB) return sortAsc ? 1 : -1;
    return 0;
  });

  console.log('Filtered users:', filteredUsers);
  console.log('Filtered users length:', filteredUsers.length);

  // Export user ke Excel
  const handleExportExcel = () => {
    try {
      const data = filteredUsers.map(u => ({
        Email: u.email || '-',
        Nama: u.displayName || '-',
        Status: getStatus(u),
        Expired: u.premiumUntil ? u.premiumUntil.split('T')[0] : (u.trialEnd ? u.trialEnd.split('T')[0] : '-'),
        'Sisa Hari': getSisaHari(u)
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'UserPremium');
      XLSX.writeFile(wb, 'user-premium-lakukelas.xlsx');
      setNotif('Export ke Excel berhasil!');
    } catch (e) {
      setNotif('Export ke Excel gagal!');
    }
  };

  // Export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text('Daftar User Premium/Trial', 14, 14);
    const data = filteredUsers.map(u => [
      u.email || '-',
      u.displayName || '-',
      getStatus(u),
      u.premiumUntil ? u.premiumUntil.split('T')[0] : (u.trialEnd ? u.trialEnd.split('T')[0] : '-'),
      getSisaHari(u)
    ]);
    autoTable(doc, {
      head: [['Email', 'Nama', 'Status', 'Expired', 'Sisa Hari']],
      body: data,
      startY: 20,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
    });
    doc.save('user-premium-lakukelas.pdf');
  };

  // Reset status premium user
  const handleResetPremium = (user) => {
    setUserToReset(user);
    setShowResetConfirm(true);
  };
  const confirmResetPremium = async () => {
    setShowResetConfirm(false);
    if (!userToReset) return;
    try {
      const res = await fetch('/api/admin/reset-premium', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userToReset.email })
      });
      if (res.ok) setNotif('Status premium berhasil direset!');
      else setNotif('Gagal reset status premium!');
    } catch {
      setNotif('Gagal reset status premium!');
    }
    setUserToReset(null);
  };

  // Fungsi hapus user premium (dummy, bisa dihubungkan ke API)
  const handleDeleteUser = async (user) => {
    setUserToDelete(user);
    setShowDeleteUserConfirm(true);
  };
  const confirmDeleteUser = async () => {
    setShowDeleteUserConfirm(false);
    if (!userToDelete) return;
    try {
      const res = await fetch('/api/admin/delete-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userToDelete.email })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setUsers(users => users.filter(u => u.email !== userToDelete.email));
        setNotif('User berhasil dihapus!');
      } else {
        setNotif(data.error || 'Gagal menghapus user!');
      }
    } catch {
      setNotif('Gagal menghapus user!');
    }
    setUserToDelete(null);
  };

  // Fungsi edit paket
  const handleEditPaket = (id) => {
    setEditPaketId(id);
    setNewPaket({
      id,
      name: hargaPaket[id]?.name || '',
      price: hargaPaket[id]?.price || '',
      diskon: hargaPaket[id]?.diskon || ''
    });
  };

  // Fungsi tambah/simpan paket (jika editPaketId ada, update; jika tidak, tambah baru)
  const handleTambahPaket = async () => {
    if (!newPaket.id || !newPaket.name || !newPaket.price) return setNotif('Lengkapi semua field paket!');
    const newHarga = { ...hargaPaket, [newPaket.id]: { name: newPaket.name, price: Number(newPaket.price), diskon: Number(newPaket.diskon) || 0 } };
    await fetch(`${API}/admin/paket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newHarga)
    });
    setHargaPaket(newHarga);
    setHargaInput(newHarga);
    setNewPaket({ id: '', name: '', price: '', diskon: '' });
    setEditPaketId(null);
    setNotif(editPaketId ? 'Paket berhasil diupdate!' : 'Paket baru berhasil ditambah!');
  };

  // Tambah user premium manual (perbaiki logika durasi paket)
  const handleTambahUserPremium = async () => {
    if (!addUserEmail || !addUserPaket) return setNotif('Isi email dan pilih paket!');
    setAddUserLoading(true);
    try {
      // Cari durasi paket otomatis
      let durasi = 30;
      const id = addUserPaket.toLowerCase();
      const bulanMatch = id.match(/(\d+)bulan/);
      const tahunMatch = id.match(/(\d+)tahun/);
      if (bulanMatch) durasi = parseInt(bulanMatch[1]) * 30;
      else if (tahunMatch) durasi = parseInt(tahunMatch[1]) * 365;
      else if (id === '6bulan') durasi = 180;
      else if (id === '12bulan') durasi = 365;
      const premiumUntil = new Date();
      premiumUntil.setDate(premiumUntil.getDate() + durasi);
      // Panggil endpoint backend untuk set premium
      const res = await fetch(`${API}/admin/set-premium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addUserEmail, paket: addUserPaket, premiumUntil: premiumUntil.toISOString() })
      });
      const data = await res.json();
      if (res.ok && data.success) setNotif('User premium berhasil ditambah!');
      else setNotif(data.error || 'Gagal menambah user premium!');
    } catch (e) {
      setNotif('Gagal menambah user premium!');
    }
    setAddUserLoading(false);
    setAddUserEmail('');
    setAddUserPaket('');
  };

  // Hapus paket
  const handleHapusPaket = (id) => {
    setPaketToDelete(id);
    setShowDeletePaketConfirm(true);
  };
  const confirmDeletePaket = async () => {
    setShowDeletePaketConfirm(false);
    if (!paketToDelete) return;
    try {
      const newHarga = { ...hargaPaket };
      delete newHarga[paketToDelete];
      await fetch(`${API}/admin/paket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newHarga)
      });
      setHargaPaket(newHarga);
      setHargaInput(newHarga);
      setNotif('Paket berhasil dihapus!');
    } catch {
      setNotif('Gagal menghapus paket!');
    }
    setPaketToDelete(null);
  };

  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600">Akses khusus admin.</div>;
  }
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <ModernLoader size={64} color="#6C4DFF" />
        <div className="text-lg text-blue-700 font-semibold mt-4">Memuat data admin...</div>
      </div>
    );
  }

  return (
    <AdminLayout menus={adminMenus} activeTab={activeTab} setActiveTab={setActiveTab}>
      <div className="w-full max-w-full box-border px-1 sm:px-2 md:px-0 md:max-w-3xl md:mx-auto pb-24 md:pb-8">
        <h1 className="text-3xl font-bold mb-6 text-center text-blue-700 dark:text-blue-300 drop-shadow">Admin Panel</h1>
        {notif && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded shadow transition-all duration-300 animate-fade-in-out">{notif}</div>}
        {/* Konten tab admin */}
        {activeTab === 'paket' && (
          <div className="mb-8 p-2 sm:p-4 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 w-full max-w-full box-border">
            <h2 className="font-semibold mb-4 text-lg text-blue-700 dark:text-blue-300">Atur Harga Paket</h2>
            {/* Form tambah/edit paket baru */}
            <div className="mb-4 flex gap-3 flex-wrap items-end">
              <input type="text" placeholder="ID Paket (ex: 1bulan)" value={newPaket.id} onChange={e => setNewPaket(p => ({ ...p, id: e.target.value }))} className="border p-2 rounded-lg w-32 focus:ring-2 focus:ring-blue-400" disabled={!!editPaketId} />
              <input type="text" placeholder="Nama Paket" value={newPaket.name} onChange={e => setNewPaket(p => ({ ...p, name: e.target.value }))} className="border p-2 rounded-lg w-40 focus:ring-2 focus:ring-blue-400" />
              <input type="number" placeholder="Harga" value={newPaket.price} onChange={e => setNewPaket(p => ({ ...p, price: e.target.value }))} className="border p-2 rounded-lg w-32 focus:ring-2 focus:ring-blue-400" />
              <input type="number" placeholder="Diskon" value={newPaket.diskon} onChange={e => setNewPaket(p => ({ ...p, diskon: e.target.value }))} className="border p-2 rounded-lg w-28 focus:ring-2 focus:ring-green-400" />
              <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow transition" onClick={handleTambahPaket}>{editPaketId ? 'Simpan Perubahan' : 'Tambah Paket'}</button>
              {editPaketId && <button className="ml-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg shadow transition" onClick={()=>{setEditPaketId(null);setNewPaket({id:'',name:'',price:'',diskon:''});}}>Batal</button>}
            </div>
            {/* Scrollable list paket, responsive grid di mobile */}
            <div className="space-y-4 md:space-y-0 md:space-x-0 overflow-x-auto max-h-[60vh] overflow-y-auto custom-scrollbar rounded-lg">
              {Object.keys(hargaInput).map(key => (
                <div key={key} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 flex flex-col md:flex-row md:items-center md:gap-3 min-w-0">
                  {/* Baris 1: Nama & harga */}
                  <div className="flex flex-col md:flex-row md:items-center md:gap-3 w-full">
                    <span className="font-medium text-gray-700 dark:text-gray-200 md:w-40 w-full">{hargaPaket[key]?.name || key}</span>
                    <span className="text-blue-700 font-bold text-lg md:ml-2 md:w-auto w-full mt-1 md:mt-0">Rp{hargaInput[key]?.price?.toLocaleString()}</span>
                  </div>
                  {/* Baris 2: Diskon & harga setelah diskon */}
                  <div className="flex flex-col sm:flex-row md:flex-row md:items-center md:gap-2 w-full mt-2 md:mt-0">
                    <input type="number" value={hargaInput[key]?.diskon || ''} onChange={e => setHargaInput(h => ({ ...h, [key]: { ...h[key], diskon: Number(e.target.value) } }))} className="border p-2 rounded-lg w-full sm:w-24 md:w-20 focus:ring-2 focus:ring-green-400 mb-2 sm:mb-0 md:mb-0" placeholder="Diskon" />
                    <span className={`font-bold text-lg ${hargaPaket[key]?.diskon > 0 ? 'text-green-700' : 'text-blue-700'} md:ml-2 md:w-auto w-full`}>Rp{((hargaInput[key]?.price || 0) - (hargaInput[key]?.diskon || 0)).toLocaleString()}</span>
                  </div>
                  {/* Tombol edit & hapus di bawah di mobile, di kanan di desktop */}
                  <div className="flex flex-col md:flex-row gap-2 mt-2 md:mt-0 w-full md:w-auto">
                    <button className="bg-yellow-400 hover:bg-yellow-500 text-white px-3 py-1 rounded text-xs w-full md:w-auto" onClick={()=>handleEditPaket(key)}>Edit</button>
                    <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs w-full md:w-auto" onClick={() => handleHapusPaket(key)}>Hapus</button>
                  </div>
                </div>
              ))}
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow mt-4 transition w-full md:w-auto" onClick={handleSimpanHarga}>Simpan</button>
          </div>
        )}
        {activeTab === 'kupon' && (
          <div className="mb-8 p-2 sm:p-4 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 w-full max-w-full box-border">
            <h2 className="font-semibold mb-4 text-lg text-blue-700 dark:text-blue-300">Atur Kupon</h2>
            <div className="mb-4 flex gap-3 flex-wrap">
              <input type="text" placeholder="Kode Kupon" value={kuponInput.code} onChange={e => setKuponInput(k => ({ ...k, code: e.target.value }))} className="border p-2 rounded-lg w-40 focus:ring-2 focus:ring-blue-400" />
              <input type="number" placeholder="Diskon" value={kuponInput.diskon} onChange={e => setKuponInput(k => ({ ...k, diskon: Number(e.target.value) }))} className="border p-2 rounded-lg w-32 focus:ring-2 focus:ring-blue-400" />
              <input type="date" placeholder="Expired" value={kuponInput.expired} onChange={e => setKuponInput(k => ({ ...k, expired: e.target.value }))} className="border p-2 rounded-lg w-40 focus:ring-2 focus:ring-blue-400" />
              <button className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow transition" onClick={handleTambahKupon}>Tambah</button>
            </div>
            <div className="overflow-x-auto rounded-lg">
              <table className="w-full text-sm rounded-lg overflow-hidden shadow">
                <thead>
                  <tr className="bg-blue-50 dark:bg-blue-900">
                    <th className="p-3 text-left">Kode</th>
                    <th className="p-3 text-left">Diskon</th>
                    <th className="p-3 text-left">Expired</th>
                    <th className="p-3 text-left">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {kuponList.map((k, i) => {
                    const expired = k.expired && new Date(k.expired) <= new Date();
                    return (
                      <tr key={i} className={expired ? 'bg-red-100 dark:bg-red-900 text-gray-400' : 'hover:bg-blue-50 dark:hover:bg-blue-800 transition'}>
                        <td className="p-3 font-mono font-semibold">{k.code}</td>
                        <td className="p-3">{k.diskon}</td>
                        <td className="p-3">{k.expired}</td>
                        <td className="p-3 font-semibold">{expired ? 'Expired' : 'Aktif'}</td>
                        <td className="p-3"><button className="text-red-600 hover:underline" onClick={() => handleHapusKupon(i)}>Hapus</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'user' && (
          <div className="mb-8 p-2 sm:p-4 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 w-full max-w-full box-border">
            <h2 className="font-semibold mb-4 text-lg text-blue-700 dark:text-blue-300">User Premium & Trial</h2>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  placeholder="Cari email/nama..."
                  value={searchUser}
                  onChange={e => setSearchUser(e.target.value)}
                  className="border p-2 rounded-lg w-48 focus:ring-2 focus:ring-blue-400"
                />
                <select
                  value={userStatusFilter}
                  onChange={e => setUserStatusFilter(e.target.value)}
                  className="border p-2 rounded-lg w-32 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="all">Semua</option>
                  <option value="premium">Premium</option>
                  <option value="trial">Trial</option>
                  <option value="nonaktif">Nonaktif</option>
                </select>
              </div>
              <div className="flex gap-2 mt-2 md:mt-0">
                <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow transition" onClick={handleExportExcel}>Export Excel</button>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow transition" onClick={handleExportPDF}>Export PDF</button>
              </div>
            </div>
            {/* MOBILE: Card list */}
            <div className="sm:hidden space-y-4">
              {filteredUsers.length === 0 && (
                <div className="p-4 text-center text-gray-400">Tidak ada user ditemukan.</div>
              )}
              {filteredUsers.map((u, i) => (
                <div key={u.email || i} className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700 p-4 flex flex-col gap-2 transition hover:shadow-md">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-blue-700 dark:text-blue-300">{i + 1}.</span>
                    <span className="font-mono text-xs break-all text-gray-700 dark:text-gray-200">{u.email || '-'}</span>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-200 font-medium">{u.displayName || '-'}</div>
                  <div className="flex items-center gap-2 text-xs">
                    {/* Status icon */}
                    {getStatus(u) === 'Premium' && <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>}
                    {getStatus(u) === 'Trial' && <span className="inline-block w-2 h-2 rounded-full bg-yellow-400"></span>}
                    {getStatus(u) === 'Nonaktif' && <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>}
                    <span className="font-semibold">{getStatus(u)}</span>
                    <span className="mx-2">|</span>
                    <span>Expired: <span className="font-mono">{u.premiumUntil ? u.premiumUntil.split('T')[0] : (u.trialEnd ? u.trialEnd.split('T')[0] : '-')}</span></span>
                    <span className="mx-2">|</span>
                    <span>Sisa: <span className="font-semibold">{getSisaHari(u)}</span></span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button className="flex-1 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-white px-3 py-2 rounded text-xs font-semibold shadow transition" onClick={()=>handleResetPremium(u)}>Reset</button>
                    <button className="flex-1 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white px-3 py-2 rounded text-xs font-semibold shadow transition" onClick={()=>handleDeleteUser(u)}>Hapus</button>
                  </div>
                </div>
              ))}
              <div className="text-xs text-gray-400 mt-2">Total: {filteredUsers.length} user</div>
            </div>
            {/* DESKTOP: Table */}
            <div className="hidden sm:block overflow-x-auto rounded-lg custom-scrollbar">
              <table className="w-full text-sm rounded-lg overflow-hidden shadow">
                <thead>
                  <tr className="bg-blue-50 dark:bg-blue-900">
                    <th className="p-3 text-left">No</th>
                    <th className="p-3 text-left cursor-pointer" onClick={()=>{setSortField('email');setSortAsc(f=>!f);}}>
                      Email {sortField==='email' && (sortAsc ? '▲' : '▼')}
                    </th>
                    <th className="p-3 text-left cursor-pointer" onClick={()=>{setSortField('displayName');setSortAsc(f=>!f);}}>
                      Nama {sortField==='displayName' && (sortAsc ? '▲' : '▼')}
                    </th>
                    <th className="p-3 text-left cursor-pointer" onClick={()=>{setSortField('status');setSortAsc(f=>!f);}}>
                      Status {sortField==='status' && (sortAsc ? '▲' : '▼')}
                    </th>
                    <th className="p-3 text-left cursor-pointer" onClick={()=>{setSortField('expired');setSortAsc(f=>!f);}}>
                      Expired {sortField==='expired' && (sortAsc ? '▲' : '▼')}
                    </th>
                    <th className="p-3 text-left">Sisa Hari</th>
                    <th className="p-3 text-left">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-gray-400">Tidak ada user ditemukan.</td>
                    </tr>
                  )}
                  {filteredUsers.map((u, i) => (
                    <tr key={u.email || i} className="hover:bg-blue-50 dark:hover:bg-blue-800 transition">
                      <td className="p-3">{i + 1}</td>
                      <td className="p-3 font-mono text-xs break-all">{u.email || '-'}</td>
                      <td className="p-3">{u.displayName || '-'}</td>
                      <td className="p-3 font-semibold flex items-center gap-2">
                        {getStatus(u) === 'Premium' && <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>}
                        {getStatus(u) === 'Trial' && <span className="inline-block w-2 h-2 rounded-full bg-yellow-400"></span>}
                        {getStatus(u) === 'Nonaktif' && <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>}
                        {getStatus(u)}
                      </td>
                      <td className="p-3">{u.premiumUntil ? u.premiumUntil.split('T')[0] : (u.trialEnd ? u.trialEnd.split('T')[0] : '-')}</td>
                      <td className="p-3">{getSisaHari(u)}</td>
                      <td className="p-3 flex flex-col gap-2 md:flex-row md:gap-2">
                        <button className="bg-yellow-400 hover:bg-yellow-500 text-white px-3 py-1 rounded text-xs" onClick={()=>handleResetPremium(u)}>Reset</button>
                        <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs" onClick={()=>handleDeleteUser(u)}>Hapus</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-xs text-gray-400 mt-2">Total: {filteredUsers.length} user</div>
            </div>
          </div>
        )}
        {activeTab === 'riwayat' && (
          <div className="p-2 sm:p-4 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 mt-8 w-full max-w-full box-border">
            <h2 className="font-semibold mb-4 text-lg text-blue-700 dark:text-blue-300">Riwayat Penggunaan Kupon</h2>
            <div className="overflow-x-auto rounded-lg">
              <table className="w-full text-sm rounded-lg overflow-hidden shadow">
                <thead>
                  <tr className="bg-blue-50 dark:bg-blue-900">
                    <th className="p-3 text-left">Kode Kupon</th>
                    <th className="p-3 text-left">Email Pengguna</th>
                    <th className="p-3 text-left">Waktu</th>
                    <th className="p-3 text-left">Paket</th>
                    <th className="p-3 text-left">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {kuponRiwayat.flatMap((k, i) =>
                    (k.riwayat || []).map((r, j) => (
                      <tr key={k.code + '-' + j}>
                        <td className="p-3 font-mono">{k.code}</td>
                        <td className="p-3">{r.email}</td>
                        <td className="p-3">{r.waktu ? r.waktu.replace('T', ' ').slice(0, 19) : '-'}</td>
                        <td className="p-3">{r.paket}</td>
                        <td className="p-3 font-mono text-xs">{r.merchant_ref}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'tambah-user' && (
          <div className="mb-8 p-2 sm:p-4 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 w-full max-w-full box-border">
            <h2 className="font-semibold mb-4 text-lg text-blue-700 dark:text-blue-300">Tambah User Premium Manual</h2>
            <div className="mb-4 flex gap-3 flex-wrap items-end">
              <input type="email" placeholder="Email User" value={addUserEmail} onChange={e => setAddUserEmail(e.target.value)} className="border p-2 rounded-lg w-56 focus:ring-2 focus:ring-blue-400" />
              <select value={addUserPaket} onChange={e => setAddUserPaket(e.target.value)} className="border p-2 rounded-lg w-40 focus:ring-2 focus:ring-blue-400">
                <option value="">Pilih Paket</option>
                {Object.entries(hargaPaket).map(([id, p]) => (
                  <option key={id} value={id}>{p.name}</option>
                ))}
              </select>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow transition" onClick={handleTambahUserPremium} disabled={addUserLoading}>{addUserLoading ? 'Memproses...' : 'Tambah User Premium'}</button>
            </div>
          </div>
        )}
        {/* Modal konfirmasi hapus dan reset tetap di luar tab */}
        <ConfirmModal open={showConfirm} onClose={() => setShowConfirm(false)} onConfirm={confirmDeleteKupon} message="Yakin ingin menghapus kupon ini?" />
        <ConfirmModal open={showResetConfirm} onClose={() => setShowResetConfirm(false)} onConfirm={confirmResetPremium} message={userToReset ? `Reset status premium untuk ${userToReset.email}?` : ''} />
        <ConfirmModal open={showDeletePaketConfirm} onClose={() => setShowDeletePaketConfirm(false)} onConfirm={confirmDeletePaket} message={paketToDelete ? `Hapus paket ${hargaPaket[paketToDelete]?.name || paketToDelete}?` : ''} />
        <ConfirmModal open={showDeleteUserConfirm} onClose={()=>setShowDeleteUserConfirm(false)} onConfirm={confirmDeleteUser} message={userToDelete ? `Yakin ingin menghapus user ${userToDelete.email}?` : ''} />
      </div>
      {/* Tambahkan custom scrollbar transparan */}
      <style>{`
      .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; background: transparent; }
      .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); border-radius: 8px; }
      .custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.08) transparent; }
      `}</style>
    </AdminLayout>
  );
} 